/**
 * A assinatura da Yard e os lockups de produto.
 *
 * A FORMA APROVADA (contrato de marca da F0, aprovado em 26/10/2026)
 *
 *   Uma forma só, a do topo do painel, em todo lugar: site, acesso, painel,
 *   e-mail e documentos. Ver `/_ds#marca` e `docs/design/F0-RELATORIO.md` §4.
 *
 *   1. "yard" em peso 700 e o produto em peso 400, colados, sem espaço:
 *      `yardcrm®`, `yardmarketing®`, `pop-upflow®`, `yardsuite®`.
 *   2. Sem bloco de cor. O grifo (o marca-texto atrás do produto) saiu da
 *      marca; o `<mark>` continua só como destaque de título.
 *   3. Sempre em `--tinta`: grafite no claro, branco no escuro. A cor da
 *      família mora EM VOLTA da marca (pílula, filete, ícone), nunca na letra,
 *      nem pela cor que o cliente escolheu para a marca dele.
 *   4. O ® acompanha toda marca de produto e a yard suite. "yard" sozinha é a
 *      assinatura da empresa e não leva.
 *   5. Endereços (myard.bio, myard.info, myard.shop) são uma palavra: "m" e o
 *      fim leves, "yard" forte, ® no fim (`MarcaEndereco`).
 *   6. Mínimo de 13 px. Abaixo disso, `SeloDeFamilia` ou o favicon.
 *
 * Antes da F1 o componente desenhava o grifo e o app.css ("Assinatura
 * compacta") desfazia o grifo dentro da `.suite-modern`. Por isso o site
 * público, fora da camada, ainda mostrava a forma antiga. Agora a forma mora
 * aqui, em estilo próprio, e sai igual em qualquer lugar, inclusive num
 * shadow root ou numa página sem o app.css.
 */

import type { CSSProperties } from 'react';
import type { Familia, Produto } from '../core/produtos';
import { COR_DA_FAMILIA, TINTA_DA_FAMILIA } from '../core/produtos';

type Tamanho = number | string;

const px = (t: Tamanho) => (typeof t === 'number' ? `${t}px` : t);

/** O corpo comum de toda marca: a mesma caixa, o mesmo aperto, a mesma tinta. */
function estiloDaMarca(tamanho: Tamanho): CSSProperties {
  return {
    fontFamily: 'var(--fonte)',
    fontSize: px(tamanho),
    fontWeight: 600,
    letterSpacing: '-.065em',
    lineHeight: 1,
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'baseline',
    color: 'var(--tinta)',
  };
}

const FORTE: CSSProperties = { fontWeight: 700 };
const LEVE: CSSProperties = { fontWeight: 400 };

type Props = {
  /** A palavra depois do prefixo. Vazia desenha só a assinatura "yard". */
  produto?: string;
  /** A palavra antes do produto. Quase sempre "yard"; o pop-up flow mora na
   *  casa do cliente e troca por "pop-up" (marca.html, 02.2). */
  prefixo?: string;
  /** A família do produto. Não pinta a marca (regra 3): vai para
   *  `data-familia`, para quem estiver em volta poder usar a cor. */
  familia?: Familia;
  /** Altura da letra: número em px ou qualquer medida CSS (`clamp()`). */
  tamanho?: Tamanho;
  /** O ® do produto. Só se desliga para nome que não é marca registrada
   *  (o "console" da porta da equipe Yard). */
  registrada?: boolean;
  className?: string;
};

export function Marca({
  produto,
  prefixo = 'yard',
  familia,
  tamanho = 20,
  registrada = true,
  className = '',
}: Props) {
  return (
    <span className={`marca ${className}`.trim()} style={estiloDaMarca(tamanho)} data-familia={familia}>
      <span style={FORTE}>{prefixo}</span>
      {produto && <span style={LEVE}>{produto}{registrada && <Registrada />}</span>}
    </span>
  );
}

/**
 * O ® das marcas de produto.
 *
 * Fica colado no fim do nome, pequeno e no alto, na medida do ® do yardhub®.
 * A medida mora aqui, e não no app.css, para o selo sair igual onde a folha
 * de estilo não chega. `aria-hidden` porque é aviso jurídico, não parte do
 * nome: o leitor de tela diria "yard crm marca registrada" em toda tela.
 */
const ESTILO_DO_R: CSSProperties = {
  fontSize: '.66em',
  fontWeight: 500,
  letterSpacing: 0,
  lineHeight: 1,
  verticalAlign: 'top',
  marginLeft: '.04em',
  position: 'relative',
  top: '-.12em',
};

export function Registrada() {
  return <sup className="marca-registrada" style={ESTILO_DO_R} aria-hidden="true">®</sup>;
}

/**
 * myard.bio, myard.info e myard.shop: as marcas que são endereço. Uma palavra
 * só, sem espaço, com "m" e o fim leves e "yard" forte no meio (decisão do
 * usuário em 26/10: .info e .shop seguem a regra do .bio).
 */
export function MarcaEndereco({
  fim,
  tamanho = 20,
  className = '',
}: {
  fim: 'bio' | 'info' | 'shop';
  tamanho?: Tamanho;
  className?: string;
}) {
  return (
    <span className={`marca marca--endereco ${className}`.trim()} style={estiloDaMarca(tamanho)} data-familia="vitrine">
      <span style={LEVE}>m</span>
      <span style={FORTE}>yard</span>
      <span style={LEVE}>.{fim}<Registrada /></span>
    </span>
  );
}

/** myard.bio. Atalho de `MarcaEndereco`, o nome que o código já usava. */
export function MarcaBio({ tamanho = 20, className = '' }: { tamanho?: Tamanho; className?: string }) {
  return <MarcaEndereco fim="bio" tamanho={tamanho} className={className} />;
}

/**
 * O lockup de UM produto do catálogo (`core/produtos.ts`), sem repetir em
 * cada tela qual é a exceção de cada um: `mesclada` vira o endereço
 * myard.bio, `prefixo` troca o "yard" (o pop-up flow), e o resto é `Marca`.
 */
export function LockupDoProduto({
  produto,
  tamanho = 18,
  className = '',
}: {
  produto: Produto;
  tamanho?: Tamanho;
  className?: string;
}) {
  if (produto.mesclada) return <MarcaBio tamanho={tamanho} className={className} />;
  return (
    <Marca
      produto={produto.sufixo}
      prefixo={produto.prefixo}
      familia={produto.familia}
      tamanho={tamanho}
      className={className}
    />
  );
}

/**
 * A marca a partir do slug de produto do site (`site-conteudo.ts`), onde as
 * vitrines são três: bio, info e loja. O site montava a marca à mão em quatro
 * lugares e escrevia "yardloja®" e "yardinfo®"; pela decisão de 26/10 os
 * dois são endereço, como o myard.bio (a loja mora em myard.shop).
 */
export function MarcaDoSlug({
  slug,
  produto,
  familia,
  tamanho = 20,
  className = '',
}: {
  slug: string;
  /** A palavra do produto quando ele é "yard" + produto ("crm", "flow"). */
  produto?: string;
  familia?: Familia;
  tamanho?: Tamanho;
  className?: string;
}) {
  if (slug === 'bio') return <MarcaEndereco fim="bio" tamanho={tamanho} className={className} />;
  if (slug === 'info') return <MarcaEndereco fim="info" tamanho={tamanho} className={className} />;
  if (slug === 'loja' || slug === 'shop') return <MarcaEndereco fim="shop" tamanho={tamanho} className={className} />;
  return (
    <Marca
      produto={produto}
      prefixo={slug === 'flow-popup' ? 'pop-up' : 'yard'}
      familia={familia}
      tamanho={tamanho}
      className={className}
    />
  );
}

/**
 * O selo quadrado do produto: a inicial sobre a cor da família.
 *
 * Serve onde não cabe o lockup inteiro (abaixo dos 13 px da marca): o item da
 * barra lateral, o cartão do catálogo, a aba do navegador. É a mesma lógica
 * do ícone de app, e é o único lugar em que a cor da família encosta na letra.
 */
export function SeloDeFamilia({
  familia,
  letra,
  tamanho = 24,
}: {
  familia: Familia;
  letra: string;
  tamanho?: number;
}) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: tamanho,
        height: tamanho,
        borderRadius: Math.round(tamanho * 0.3),
        background: COR_DA_FAMILIA[familia],
        color: TINTA_DA_FAMILIA[familia],
        display: 'grid',
        placeItems: 'center',
        fontWeight: 800,
        fontSize: Math.round(tamanho * 0.52),
        letterSpacing: '-.05em',
        flexShrink: 0,
      }}
    >
      {letra}
    </span>
  );
}
